import Vue from 'vue';
import VueRouter from 'vue-router';
import StationLocator from '@/components/StationLocator.vue';
import StationShow from '@/components/StationShow.vue';
import StationEdit from '@/components/StationEdit.vue';
import StationNew from '@/components/StationNew.vue';
import store from '@/store';

Vue.use(VueRouter);

const CA_PROVINCE_CODES = {
  AB: true,
  BC: true,
  MB: true,
  NB: true,
  NL: true,
  NS: true,
  NT: true,
  NU: true,
  ON: true,
  PE: true,
  QC: true,
  SK: true,
  YT: true,
};

const routes = [
  {
    path: '/',
    redirect: '/find/nearest',
    name: 'StationLocator',
    component: StationLocator,
    children: [
      {
        path: '/station/new',
        name: 'StationNew',
        component: StationNew,
      },
      {
        path: '/station/:id',
        name: 'StationShow',
        component: StationShow,
      },
      {
        path: '/station/:id/edit',
        name: 'StationEdit',
        component: StationEdit,
      },
      {
        path: '/find',
        redirect: '/find/nearest',
      },
      {
        path: '/find/nearest',
        name: 'FindNearestStations',
      },
      {
        path: '/find/route',
        name: 'FindRouteStations',
      },
      {
        path: '/analyze',
        name: 'AnalyzeStations',
      },
      {
        path: '/corridors',
        name: 'CorridorStations',
      },
    ],
  },
];

const router = new VueRouter({
  base: '/',
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.query) {
    // Redirect from legacy regions without country codes, to ISO 3166-2 codes
    // that include unambiguous country codes. For example, redirect from
    // `region=CO` to `region=US-CO` or `region=BC` to `region=CA-BC`.
    if (to.query.region && !to.query.region.includes('-')) {
      let country = 'US';
      if (CA_PROVINCE_CODES[to.query.region]) {
        country = 'CA';
      }

      return next({
        replace: true,
        path: to.path,
        params: to.params,
        query: {
          ...to.query,
          region: `${country}-${to.query.region}`,
        },
      });
    }

    // Redirect from legacy URLs using `show_public`, `show_private`, and
    // `show_planned` (which have now become `access` and `status`).
    if (to.query.show_public !== undefined || to.query.show_private !== undefined || to.query.show_planned !== undefined) {
      const redirectQuery = { ...to.query };
      delete redirectQuery.show_public;
      delete redirectQuery.show_private;
      delete redirectQuery.show_planned;

      redirectQuery.access = [];
      if (String(to.query.show_public) === 'true' || to.query.show_public === undefined) {
        redirectQuery.access.push('public');
      }
      if (String(to.query.show_private) === 'true') {
        redirectQuery.access = ['private'];
      }
      if (redirectQuery.access.length === 1 && redirectQuery.access[0] === 'public') {
        delete redirectQuery.access;
      } else if (redirectQuery.access.length === 0) {
        redirectQuery.access = null;
      }

      if (String(to.query.show_planned) === 'true') {
        redirectQuery.status = ['E', 'P'];
      }

      return next({
        replace: true,
        path: to.path,
        params: to.params,
        query: redirectQuery,
      });
    }
  }

  return next();
});

router.afterEach((to) => {
  let routeName = to.name;

  // On each route change, keep track of the current tab and find mode last
  // used. Not setting the current tab for the station show/edit/add routes
  // maintains the current tab.
  const navPayload = {};
  if (routeName === 'FindNearestStations' || routeName === 'FindRouteStations') {
    navPayload.activeTab = 'find';

    if (routeName === 'FindNearestStations') {
      navPayload.activeFindMode = 'nearest';
    } else if (routeName === 'FindRouteStations') {
      navPayload.activeFindMode = 'route';
    }
  } else if (routeName === 'CorridorStations') {
    navPayload.activeTab = 'corridors';
  } else if (routeName === 'AnalyzeStations') {
    navPayload.activeTab = 'analyze';
  }
  store.commit('setNavActive', navPayload);
  if (to.query.nps_funded) {
    routeName = 'NpsStations';
  }

  // When the query params change due to filter changes, store these so they
  // can be persisted for other uses (eg, remembering the filters when
  // switching between tabs, or navigating to and from the show page).
  if (routeName === 'FindNearestStations' || routeName === 'FindRouteStations' || routeName === 'AnalyzeStations' || routeName === 'CorridorStations' || routeName === 'NpsStations') {
    store.commit('setFilters', {
      scope: routeName,
      filters: to.query,
    });
  }
});

export default router;
